import pageTitle from 'ember-page-title/helpers/page-title';
import TuitionAssistanceDependentTable from 'tio-employee/components/tuition-assistance/dashboard/dependent-table';
import AddDependent from 'tio-employee/components/tuition-assistance/forms/add-dependents';
import TioHeader from 'tio-common/components/tio/page/hero/index';
import TuitionAssistanceDashboardManageDependents from 'tio-employee/components/tuition-assistance/dashboard/manage-dependents';
import Component from '@glimmer/component';
import { Header, Section, VStack } from 'tio-reimagined/components/layout';
import { t } from 'ember-intl';
import { LinkTo } from '@ember/routing';
import { hash } from '@ember/helper';
import { eq } from 'tio-ui/utilities';
import { htmlSafe } from '@ember/template';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import ActivityCard from 'tio-employee/components/reimagined/tas/activity-card';
import type EmployeeModel from 'tio-data/models/employee';
import type {
  EligibleTemplate,
  LiveTasApplication,
  LiveTasData,
  LiveTasProgram,
} from 'tio-employee/services/reimagined-data';
import type { AmountInfo } from 'tio-employee/utils/tas/activity-helpers';
import { isActiveInstanceState } from 'tio-common/utils/tuition-assistance/application-states';
import { PhArrowRight } from 'ember-phosphor-icons';

interface DependentProgramsModel {
  tasData: LiveTasData;
  eligibleTemplates: EligibleTemplate[];
  employee: EmployeeModel;
}

export interface TasDependentProgramDashboardSignature {
  Args: {
    model: DependentProgramsModel;
  };
}

/** Terminal negative states — hide after 1 month */
const STALE_FILTERABLE_STATES = new Set([
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
  'TAS.ApplicationState.REJECTED_PER_POLICY',
  'TAS.ApplicationState.ABANDONED',
]);

const PAID_APPLICATION_STATES = new Set([
  'TAS.ApplicationPaymentState.PAID',
  'TAS.ApplicationPaymentState.PAID_IN_FULL',
]);

const PAID_PAYMENT_STATES = new Set([
  'TAS.ApplicationPaymentState.PAID',
  'TAS.ApplicationPaymentState.PAID_IN_FULL',
  'TAS.ApplicationPaymentState.PAID_IN_PART',
]);

const PENDING_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

const REQUESTED_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
]);

const REPAYMENT_PAYMENT_STATE = 'TAS.ApplicationPaymentState.REFUND_REQUESTED';

const PENDING_PROGRAM_STATES = new Set([
  'TAS.ProgramInstanceState.CREATED',
  'TAS.ProgramInstanceState.DRAFT',
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
]);

function canAddRequests(state: string) {
  return !PENDING_PROGRAM_STATES.has(state);
}

type ActivityItem =
  | { kind: 'application'; data: LiveTasApplication; sortDate: string }
  | { kind: 'program'; data: LiveTasProgram; sortDate: string };

export default class TasDependentProgramDashboard extends Component<TasDependentProgramDashboardSignature> {
  get programs(): LiveTasProgram[] {
    return this.args.model.tasData.programs.filter(
      (p) => isActiveInstanceState(p.state) && p.dependentId !== null
    );
  }

  get applications(): LiveTasApplication[] {
    return this.args.model.tasData.applications.filter(
      (app) => app.recordType === 'application' && app.dependentId !== null
    );
  }

  get eligibleTemplates(): EligibleTemplate[] {
    return this.args.model.eligibleTemplates ?? [];
  }

  /** Count applications belonging to a given program instance */
  programRequestCount = (programId: string): number =>
    this.args.model.tasData.applications.filter(
      (app) => app.recordType === 'application' && app.programInstanceId === programId
    ).length;

  get activityItems(): ActivityItem[] {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const visibleApps = this.applications.filter((app) => {
      if (app.paymentState === REPAYMENT_PAYMENT_STATE) return true;
      if (STALE_FILTERABLE_STATES.has(app.state)) {
        const lastAction = app.updatedAt ?? app.createdAt;
        return new Date(lastAction) >= oneMonthAgo;
      }
      if (
        app.state === 'TAS.ApplicationState.FULFILLED' &&
        PAID_PAYMENT_STATES.has(app.paymentState)
      ) {
        const lastAction = app.updatedAt ?? app.createdAt;
        return new Date(lastAction) >= oneMonthAgo;
      }
      return true;
    });

    const instancesWithApps = new Set(
      visibleApps.map((app) => app.programInstanceId).filter((id): id is string => id !== null)
    );

    const standalonePrograms = this.programs.filter((p) => !instancesWithApps.has(p.id));

    const items: ActivityItem[] = [];

    for (const app of visibleApps) {
      items.push({
        kind: 'application',
        data: app,
        sortDate: app.updatedAt ?? app.createdAt,
      });
    }

    for (const program of standalonePrograms) {
      items.push({
        kind: 'program',
        data: program,
        sortDate: program.updatedAt ?? '',
      });
    }

    items.sort((a, b) => (b.sortDate > a.sortDate ? 1 : b.sortDate < a.sortDate ? -1 : 0));

    return items;
  }

  applicationAmountInfo = (application: LiveTasApplication): AmountInfo | null => {
    const isRequestedState = REQUESTED_APPLICATION_STATES.has(application.state);
    const reimbursementApprovedTotal = application.approvedTotal;
    const reimbursementRequestedTotal = application.requestedTotal;

    const courseCashApprovedTotal = isRequestedState
      ? (application.courseCashPreApprovedTotal ?? application.courseCashFinalApprovedTotal ?? 0)
      : (application.courseCashFinalApprovedTotal ?? application.courseCashPreApprovedTotal ?? 0);

    const courseCashUsed = application.courseCashUsed ?? 0;
    const courseCashReserved = application.courseCashReserved ?? 0;

    const isCourseCashApp =
      courseCashApprovedTotal > 0 || courseCashUsed > 0 || courseCashReserved > 0;

    let courseCashAmount = courseCashUsed;
    let courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_used';
    if (courseCashUsed <= 0 && courseCashReserved > 0) {
      courseCashAmount = courseCashReserved;
      courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_reserved';
    } else if (courseCashUsed <= 0 && courseCashReserved <= 0 && courseCashApprovedTotal > 0) {
      courseCashAmount = courseCashApprovedTotal;
      courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_requested';
    }

    if (application.paymentState === REPAYMENT_PAYMENT_STATE) {
      const amount = application.approvedTotal;
      if (amount <= 0) return null;
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_repayment',
        courseCashAmount,
        courseCashLabelKey,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (
      PAID_APPLICATION_STATES.has(application.paymentState) ||
      application.paymentState === 'TAS.ApplicationPaymentState.PAID_IN_PART'
    ) {
      const amount = application.paidTotal || application.approvedTotal;
      if (amount <= 0) return null;
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_paid',
        courseCashAmount,
        courseCashLabelKey,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (PENDING_APPLICATION_STATES.has(application.state)) {
      const amount = reimbursementApprovedTotal;
      if (amount <= 0 && reimbursementRequestedTotal <= 0 && !isCourseCashApp) return null;
      return {
        reimbursementAmount: amount > 0 ? amount : reimbursementRequestedTotal,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_requested',
        courseCashAmount,
        courseCashLabelKey,
        hasCourseCash: isCourseCashApp,
      };
    }

    const reimbursementAmount =
      reimbursementApprovedTotal > 0 ? reimbursementApprovedTotal : reimbursementRequestedTotal;
    if (reimbursementAmount <= 0 && !isCourseCashApp) return null;

    return {
      reimbursementAmount,
      reimbursementLabelKey:
        reimbursementApprovedTotal > 0
          ? 'tuition_assistance.reimagined.index.application_amount_approved'
          : 'tuition_assistance.reimagined.index.application_amount_requested',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  };

  itemProgramName = (item: ActivityItem): string | null =>
    item.kind === 'application' ? item.data.programName : null;

  itemAmountInfo = (item: ActivityItem) =>
    item.kind === 'application' ? this.applicationAmountInfo(item.data) : null;

  itemInstitution = (item: ActivityItem): string | null =>
    item.kind === 'program' ? item.data.institution : null;

  <template>
    {{pageTitle (t "dashboard")}}
    <VStack>
      <Header>{{t "tuition_assistance.default"}}</Header>

      {{! ── Section 1: Dependents Management ── }}
      <Section>
        <:header>{{t "common.tuition_assistance.dependent_programs"}}</:header>
        <:body>
          {{#if @model.employee.hasDependents}}
            <h3 class="font-semibold my-2">{{t
                "common.tuition_assistance.dependent_table.table_title"
              }}</h3>
            <TuitionAssistanceDependentTable @employee={{@model.employee}} />
            <div class="flex -ml-2 mt-4">
              <AddDependent @employee={{@model.employee}} class="justify-end" />
            </div>
          {{else}}
            <div class="flex">
              <div class="w-1/2">
                <TioHeader
                  @headerBeginning="Apply for"
                  @highlightText="Tuition Assistance"
                  @headerEnd="Apply for Dependent Tuition Assistance to further your dependents’ training and education."
                  @description="Take control of your professional development, expand your opportunities, and lead a fulfilling and meaningful life through continued education. Your dependents are also eligible!"
                />
              </div>
              <div class="tio-flex-center w-1/2">
                <TuitionAssistanceDashboardManageDependents @employee={{@model.employee}} />
              </div>
            </div>
          {{/if}}
        </:body>
      </Section>
      {{! ── Section 2: Activity (applications & programs) ── }}
      {{#if this.activityItems.length}}
        <Section>
          <:header>
            <div class="tio-row-between">
              <span>{{t "common.active"}}</span>
              <div class="tio-row">
                <LinkTo
                  @route="authenticated.tas.benefit-history"
                  @query={{hash dependents="true"}}
                  class="tio-copy text-sm font-semibold"
                >
                  {{t "tuition_assistance.reimagined.dashboard.link_view_applications"}}
                </LinkTo>
              </div>
            </div>
          </:header>
          <:body>
            <div class="grid grid-cols-1 gap-4">
              {{#each this.activityItems as |item|}}
                {{#if (eq item.kind "application")}}
                  <ActivityCard
                    @route="authenticated.tas.applications.show"
                    @model={{item.data.id}}
                    @name={{item.data.name}}
                    @state={{item.data.state}}
                    @shortId={{item.data.id}}
                    @programName={{this.itemProgramName item}}
                    @updatedAt={{item.data.updatedAt}}
                    @amountInfo={{this.itemAmountInfo item}}
                  />
                {{else}}
                  <ActivityCard
                    @route="authenticated.tas.programs.show"
                    @model={{item.data.id}}
                    @name={{item.data.name}}
                    @state={{item.data.state}}
                    @shortId={{item.data.id}}
                    @iconKind="program"
                    @institution={{this.itemInstitution item}}
                    @updatedAt={{item.data.updatedAt}}
                  >
                    <:metrics>
                      {{#if (canAddRequests item.data.state)}}
                        <div class="hidden shrink-0 md:flex">
                          <div
                            class="rounded-md border tio-border-brand px-2 py-0.5 text-left leading-tight"
                          >
                            <span class="text-[10px] font-medium tio-fg-secondary">
                              {{t
                                "tuition_assistance.reimagined.dashboard.activity_requests_label"
                              }}
                            </span>
                            <div class="text-xs font-bold tio-fg-primary">
                              {{this.programRequestCount item.data.id}}
                            </div>
                            <span class="text-[10px] tio-fg-brand">
                              {{t
                                "tuition_assistance.reimagined.dashboard.activity_requests_add_now"
                              }}
                            </span>
                          </div>
                        </div>
                      {{/if}}
                    </:metrics>
                  </ActivityCard>
                {{/if}}
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}
      {{! ── Section 3: Eligible Benefits (card grid) ── }}
      {{#if this.eligibleTemplates.length}}
        <Section>
          <:header>{{t "tas.dashboard.available_benefits"}}</:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {{#each this.eligibleTemplates as |template|}}
                <LinkTo
                  @route="authenticated.tas.getting-started"
                  @model={{template.id}}
                  class="block rounded-lg border tio-border-default p-5 hover:shadow-md transition-shadow"
                >
                  <div class="tio-row-start-between gap-2">
                    <div class="min-w-0">
                      <Heading @level={{4}} class="truncate">{{template.name}}</Heading>
                      {{#if template.description}}
                        <Text @variant="muted" class="mt-1 line-clamp-2">
                          {{htmlSafe template.description}}
                        </Text>
                      {{/if}}
                    </div>
                    <PhArrowRight @size={{20}} class="shrink-0 text-ocean-600 mt-0.5" />
                  </div>
                </LinkTo>
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}
    </VStack>
  </template>
}
